import React, { useState } from "react";
import { X, UserCheck, Loader2 } from "lucide-react";
import { Button } from "../misc/Button";
import useAppDispatch from "../../hooks/useAppDispatch";
import { stopImpersonation } from "../../store/authSlice";
import useAppSelector from "../../hooks/useAppSelector";
import { toast } from "react-toastify";
import CustomToast from "./CustomToast";
import axios from "axios";
import { getAuthHeader } from "../../utils/authHeader";
import { resetImpersonationExpiredFlag } from "../../utils/axiosConfig";
import ConfirmPopup from "./ConfirmPopup";
import { useIsMobile } from "../../hooks/useIsMobile";

const ImpersonationBanner: React.FC = () => {
    const dispatch = useAppDispatch();
    const { isImpersonating, impersonatedUser, sessionId, impersonationExpired } =
        useAppSelector((state) => state.auth);
    const [isExiting, setIsExiting] = useState(false);
    const isMobile = useIsMobile(900);

    if (!isImpersonating || !impersonatedUser) {
        return null;
    }

    const handleExitImpersonation = async () => {
        if (!sessionId) {
            console.error("No sessionId found for impersonation");
            resetImpersonationExpiredFlag();
            dispatch(stopImpersonation());
            return;
        }

        setIsExiting(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            await axios.post(
                `${backendUrl}/admin/exit-impersonation`,
                { sessionId },
                { headers }
            );

            resetImpersonationExpiredFlag();
            dispatch(stopImpersonation());

            toast.success(
                <CustomToast
                    message="Exited impersonation mode"
                    type="success"
                />,
                { autoClose: 2000 }
            );
        } catch (error: unknown) {
            console.error("Failed to exit impersonation:", error);
            // Still exit locally — session is already expired
            resetImpersonationExpiredFlag();
            dispatch(stopImpersonation());
        } finally {
            setIsExiting(false);
        }
    };

    return (
        <>
            {/* Impersonation session expired — forced exit, both buttons call exit */}
            <ConfirmPopup
                open={impersonationExpired}
                title="Impersonation Session Expired"
                message="Your impersonation session has expired. You must exit impersonation mode to continue."
                messageTwo="Click below to return to your account."
                confirmText={isExiting ? "Exiting..." : "Exit Impersonation"}
                confirmVariant="destructive"
                onConfirm={handleExitImpersonation}
                onCancel={handleExitImpersonation}
                hideCancelButton={true}
            />

            {/* Active impersonation banner */}
            <div className="bg-[#ffbe00] text-gray-900 px-4 py-3 flex items-center justify-between shadow-md z-50">
                <div className="flex items-center gap-3">
                    <UserCheck className={isMobile ? "w-7 h-7" : "w-5 h-5"} />
                    <span className="font-semibold">
                        Viewing as:{" "}
                        <span className="font-bold">
                            {impersonatedUser.nameFirst} {impersonatedUser.nameLast}
                        </span>{" "}
                        ({impersonatedUser.email})
                    </span>
                </div>
                <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleExitImpersonation}
                    disabled={isExiting}
                    className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 -mt-1"
                >
                    {isExiting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {isMobile ? "Exit" : "Exiting..."}
                        </>
                    ) : (
                        <>
                            <X className="w-4 h-4" />
                            {isMobile ? "Exit" : "Exit Impersonation"}
                        </>
                    )}
                </Button>
            </div>
        </>
    );
};

export default ImpersonationBanner;
